import { pickupTrackingLink } from './ClickCollectPickupTrackingView';
import { scheduleLabel } from './beclickSchedule';
import './BeclickSchedule.css';
import StockRecipeChoices, { stockChoiceFor, stockChoiceValid, stockChoiceSignature, stockChoiceSummary, withInitialStockChoice, menuStockSignature, stockRecipeError } from './StockRecipeChoices';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { API_URL, resolveMediaUrl } from './api';
import ClickCollectIdentity, { readCustomerIdentityToken, saveCustomerIdentityToken } from './ClickCollectIdentity';
import BefidCustomerIdentityPanel from './BefidCustomerIdentityPanel';
import './CustomerJourneys.css';

const CC_CUSTOMER_SESSION_KEY = 'beborne_click_collect_customer';
const PRODUCT_ALLERGEN_LABELS = {
  gluten: 'Céréales contenant du gluten',
  crustaceans: 'Crustacés',
  eggs: 'Œufs',
  fish: 'Poissons',
  peanuts: 'Arachides',
  soy: 'Soja',
  milk: 'Lait',
  nuts: 'Fruits à coque',
  celery: 'Céleri',
  mustard: 'Moutarde',
  sesame: 'Graines de sésame',
  sulphites: 'Anhydride sulfureux et sulfites',
  lupin: 'Lupin',
  molluscs: 'Mollusques',
};

function formatCurrency(value) {
  return `${Number(value || 0).toFixed(2)} €`;
}

function formatDescription(value) {
  return String(value || '')
    .replace(/%5C?n/gi, ' ')
    .replace(/\\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function useQuery() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

function getCanonicalSectionName(name) {
  const normalized = String(name || '').trim().toLowerCase();
  if (['entrée', 'entree', 'entrées', 'entrees'].includes(normalized)) return 'Entrée';
  if (['plat', 'plats'].includes(normalized)) return 'Plat';
  if (['dessert', 'desserts'].includes(normalized)) return 'Dessert';
  if (['boisson', 'boissons'].includes(normalized)) return 'Boisson';
  if (['side', 'sides'].includes(normalized)) return 'Side';
  return String(name || '').trim();
}

function getSectionRequiredQuantity(section) {
  return Math.max(1, parseInt(section?.MenuSections?.requiredQuantity || section?.requiredQuantity || 1, 10) || 1);
}

function groupSupplements(product) {
  return Object.values((product?.Supplements || []).reduce((groups, supplement) => {
    const key = supplement.SupplementCategory?.name || 'Suppléments';
    if (!groups[key]) groups[key] = { name: key, items: [] };
    groups[key].items.push(supplement);
    return groups;
  }, {}));
}

function ClickCollectStatus({ status }) {
  const { slug } = useParams();
  const query = useQuery();
  const orderId = query.get('order');
  const token = query.get('token');
  const [tracking, setTracking] = useState(null);
  const [confirmed, setConfirmed] = useState(null), [checkError, setCheckError] = useState(''), [reload, setReload] = useState(0), [checking, setChecking] = useState(false);
  useEffect(() => {
    setConfirmed(null); setTracking(null); setCheckError('');
    if (status !== 'success' || !token || !/^[1-9]\d*$/.test(orderId || '')) return undefined;
    const controller = new AbortController();
    setChecking(true);
    fetch(`${API_URL}/api/click-collect/orders/${encodeURIComponent(orderId)}/status?token=${encodeURIComponent(token)}`, { cache: 'no-store', signal: controller.signal })
      .then(async response => { const data = await response.json(); if (controller.signal.aborted) return; if (!response.ok || Number(data.order?.id) !== Number(orderId)) throw Error('La confirmation ne peut pas être vérifiée. Contactez le restaurant en cas de doute.'); setConfirmed(data.order); setTracking(data.tracking || null); })
      .catch(failure => { if (!controller.signal.aborted) setCheckError(failure.message); })
      .finally(() => { if (!controller.signal.aborted) setChecking(false); });
    return () => controller.abort();
  }, [status, orderId, token, reload]);
  return (
    <main className="clickcollect-page clickcollect-status-page">
      <section className="clickcollect-status-card">
        <span className="clickcollect-pill">Click & collect</span>
        <h1>{confirmed?.paymentStatus === 'paid' ? 'Commande payée' : status === 'success' ? 'Retour du paiement' : 'Paiement interrompu'}</h1>
        <p>
          {confirmed?.paymentStatus === 'paid' ? 'Votre paiement est confirmé par le restaurant.' : status === 'success'
            ? 'Le traitement du paiement peut prendre quelques instants. Ce retour ne confirme pas son encaissement : consultez la confirmation reçue ou contactez le restaurant avec votre numéro de commande.'
            : 'Le paiement a été interrompu. En cas de doute sur un débit, vérifiez auprès du restaurant avant de repasser commande.'}
        </p>
        {orderId ? <strong>Commande #{orderId}</strong> : null}
        {confirmed && scheduleLabel(confirmed) && <p><strong>{scheduleLabel(confirmed)}</strong></p>}
        {confirmed?.schedule?.state === 'reconciliation_required' && <p>Votre paiement est reçu, mais le créneau nécessite une confirmation du restaurant. Contactez-le avant de vous déplacer et ne repassez pas commande.</p>}
        {checking && <p role="status">Vérification de la commande…</p>}{checkError && <p role="alert">{checkError}</p>}
        {status === 'success' && token && confirmed?.paymentStatus !== 'paid' && <button type="button" disabled={checking} onClick={() => setReload(value => value + 1)}>Vérifier la confirmation</button>}
        {confirmed?.paymentStatus === 'paid' && pickupTrackingLink(tracking, slug, orderId) && <a className="clickcollect-primary" href={pickupTrackingLink(tracking, slug, orderId)} referrerPolicy="no-referrer">Suivre mon retrait</a>}
        <a className="clickcollect-primary" href={`/c/${encodeURIComponent(slug)}`}>Retour au menu</a>
      </section>
    </main>
  );
}

function ClickCollectView({ embedded = false } = {}) {
  const { slug } = useParams();
  const query = useQuery();
  const [payload, setPayload] = useState(null);
  const [cart, setCart] = useState([]);
  const [activeSectionId, setActiveSectionId] = useState('');
  const [activeSubcategoryId, setActiveSubcategoryId] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cartOpen, setCartOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerComment, setCustomerComment] = useState('');
  const [fulfillmentType, setFulfillmentType] = useState('pickup');
  const [availability, setAvailability] = useState(null);
  const [availabilityError, setAvailabilityError] = useState('');
  const [pickupMode, setPickupMode] = useState('immediate');
  const [pickupAt, setPickupAt] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [availabilityReload, setAvailabilityReload] = useState(0);
  const availabilityRequest = useRef(0);
  const pickupReady = fulfillmentType === 'delivery' || (availability && !availabilityError && (availability.configured === false || (pickupMode === 'immediate' ? availability.immediate?.available === true : availability.slots?.some(slot => slot.pickupAt === pickupAt && slot.available))));
  function changePickup(mode, at = '', date = pickupDate) {
    setPickupMode(mode); setPickupAt(at); setPickupDate(date); setCart([]); setCartOpen(false); setDetailProduct(null); setSelectedMenu(null); setUpsellOpen(false); setUpsellSeen(false); setAppliedPromoCode(null); orderAttemptRef.current = null;
  }
  function changeFulfillment(mode) { if (mode === fulfillmentType) return; changePickup('immediate', '', ''); setFulfillmentType(mode); setDeliveryQuote(null); }

  const [deliveryAddress, setDeliveryAddress] = useState({ addressLine1: '', addressLine2: '', postalCode: '', city: '', country: 'FR', instructions: '' });
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [deliveryQuoteLoading, setDeliveryQuoteLoading] = useState(false);
  const [deliveryQuoteError, setDeliveryQuoteError] = useState('');
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [identityToken, setIdentityToken] = useState('');
  const [identitySlug, setIdentitySlug] = useState('');
  const [useLoyalty, setUseLoyalty] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountFirstName, setAccountFirstName] = useState('');
  const [accountError, setAccountError] = useState('');
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState(null);
  const [promoCodeError, setPromoCodeError] = useState('');
  const [detailProduct, setDetailProduct] = useState(null);
  const [selectedSupplementIds, setSelectedSupplementIds] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [menuSelections, setMenuSelections] = useState({});
  const [menuSelectionError, setMenuSelectionError] = useState('');
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [upsellSeen, setUpsellSeen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const sectionRefs = useRef({});
  const tabRefs = useRef({});
  const submittingRef = useRef(false);
  const orderAttemptRef = useRef(null);
  const deliveryQuoteRequestRef = useRef(0);
  const identityRequestRef = useRef(0);
  const activeRef = useRef(true);
  const scopeRef = useRef(slug);
  scopeRef.current = slug;

  useEffect(() => {
    activeRef.current = true;
    return () => { activeRef.current = false; };
  }, []);

  useEffect(() => {
    orderAttemptRef.current = null;
    setCurrentCustomer(null);
    setIdentityToken('');
    setUseLoyalty(false);
    setCustomerPhone('');
    setCustomerName('');
    setCustomerComment('');
    setCart([]);
    setCartOpen(false);
    setPayload(null);
    setActiveSectionId('');
    setActiveSubcategoryId('all');
    setDeliveryQuote(null);
    setDeliveryAddress({ addressLine1: '', addressLine2: '', postalCode: '', city: '', country: 'FR', instructions: '' });
    setFulfillmentType('pickup');
    setAvailability(null); setAvailabilityError(''); setPickupMode('immediate'); setPickupAt(''); setPickupDate('');
    setAppliedPromoCode(null);
    setPromoCodeInput('');
    setPromoCodeError('');
    setAccountOpen(false);
    setAccountFirstName('');
    setAccountError('');
    setDetailProduct(null);
    setSelectedMenu(null);
    setMenuSelections({});
    setUpsellOpen(false);
    setUpsellSeen(false);
    setError('');
    deliveryQuoteRequestRef.current += 1;
    try {
      const stored = JSON.parse(localStorage.getItem(`${CC_CUSTOMER_SESSION_KEY}_${slug}`) || 'null');
      if (stored?.id) {
        const customerReference = { id: stored.id };
        setCurrentCustomer(customerReference);
        // Replace older cached profiles, which could include private server fields.
        localStorage.setItem(`${CC_CUSTOMER_SESSION_KEY}_${slug}`, JSON.stringify(customerReference));
      }
    } catch (err) {
      try { localStorage.removeItem(`${CC_CUSTOMER_SESSION_KEY}_${slug}`); } catch {}
    }
  }, [slug]);

  useEffect(() => {
    let active = true;
    const requestId = ++identityRequestRef.current;
    const token = readCustomerIdentityToken(slug);
    if (!token) return () => { active = false; };
    fetch(`${API_URL}/api/click-collect/${encodeURIComponent(slug)}/identity/session`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.customer?.loyaltyEnabled !== true) throw Error('Session expirée.');
        if (!active || requestId !== identityRequestRef.current) return;
        setIdentityToken(token); setIdentitySlug(slug); setCurrentCustomer(data.customer);
        setCustomerName(data.customer.firstName || ''); setCustomerPhone(data.customer.phone || '');
      })
      .catch(() => { if (active && requestId === identityRequestRef.current) saveCustomerIdentityToken(slug, ''); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (query.get('account') === '1' || query.get('source') === 'beguest') {
      setAccountOpen(true);
    }
  }, [query]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetch(`${API_URL}/api/click-collect/${encodeURIComponent(slug)}/menu${fulfillmentType === 'pickup' && pickupMode === 'scheduled' && pickupAt ? `?pickupMode=scheduled&pickupAt=${encodeURIComponent(pickupAt)}` : ''}`, { cache: 'no-store' })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || 'Menu indisponible.');
        if (mounted) {
          setPayload(data);
          const firstSectionId = data?.sections?.[0]?.id || 'menus';
          setActiveSectionId((current) => current || firstSectionId);
        }
      })
      .catch((err) => mounted && setError(err.message || 'Menu indisponible.'))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [slug, fulfillmentType, pickupMode, pickupAt]);

  useEffect(() => {
    const request = ++availabilityRequest.current;
    const controller = new AbortController();
    setAvailability(null); setAvailabilityError('');
    fetch(`${API_URL}/api/click-collect/${encodeURIComponent(slug)}/availability${pickupDate ? `?date=${encodeURIComponent(pickupDate)}` : ''}`, { cache: 'no-store', signal: controller.signal })
      .then(async response => {
        const data = await response.json();
        if (!response.ok || typeof data.configured !== 'boolean') throw Error('Horaires momentanément indisponibles. Actualisez avant de commander.');
        if (request !== availabilityRequest.current || controller.signal.aborted) return;
        setAvailability(data);
        if (data.configured && !data.allowedModes?.includes(pickupMode)) { setPickupMode(data.allowedModes?.[0] || 'scheduled'); setPickupAt(''); }
      })
      .catch(failure => { if (request === availabilityRequest.current && !controller.signal.aborted) setAvailabilityError(failure.message); });
    return () => controller.abort();
    // Mode changes do not change the available slots.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, pickupDate, availabilityReload]);
  const products = useMemo(() => payload?.products || [], [payload]);
  const menus = useMemo(() => payload?.menus || [], [payload]);
  const sections = useMemo(() => payload?.sections || [], [payload]);
  const subcategories = useMemo(() => payload?.subcategories || [], [payload]);
  const activeSection = sections.find((section) => String(section.id) === String(activeSectionId));
  const recommendationProducts = products
    .filter((product) => product.isDealOfDay || ['Plat', 'Dessert', 'Boisson'].includes(getCanonicalSectionName(product.Section?.name)))
    .slice(0, 8);
  const sectionSubcategories = subcategories.filter((subcategory) => String(subcategory.sectionId) === String(activeSectionId));
  const filteredProducts = products.filter((product) => {
    const query = searchQuery.trim().toLowerCase();
    if (query) return `${product.name || ''} ${product.description || ''}`.toLowerCase().includes(query);
    if (!activeSectionId || activeSectionId === 'menus') return false;
    if (String(product.sectionId || product.Section?.id || '') !== String(activeSectionId)) return false;
    if (activeSubcategoryId === 'all') return true;
    return String(product.subcategoryId || '') === String(activeSubcategoryId);
  });
  const visibleMenuSections = selectedMenu
    ? (selectedMenu.Sections || []).map((section) => ({
      ...section,
      requiredQuantity: getSectionRequiredQuantity(section),
      products: (selectedMenu.Products || []).filter((product) => String(product.sectionId) === String(section.id)),
    }))
    : [];
  const browseGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      return [{ id: 'search', title: 'Résultats', products: filteredProducts }];
    }
    if (activeSubcategoryId === 'recommended') {
      return [{ id: 'recommended', title: 'Notre sélection pour vous', products: recommendationProducts }];
    }
    if (activeSubcategoryId !== 'all') {
      return [{
        id: `section-${activeSectionId}`,
        title: activeSection?.name || 'Menu',
        products: filteredProducts,
      }];
    }
    return sections
      .map((section) => ({
        id: `section-${section.id}`,
        sectionId: section.id,
        title: section.name,
        products: products.filter((product) => String(product.sectionId || product.Section?.id || '') === String(section.id)),
      }))
      .filter((group) => group.products.length > 0);
  }, [activeSection?.name, activeSectionId, activeSubcategoryId, filteredProducts, products, recommendationProducts, searchQuery, sections]);

  useEffect(() => {
    if (searchQuery.trim() || activeSubcategoryId !== 'all') return undefined;
    let ticking = false;
    const resolveActiveSectionFromScroll = () => {
      ticking = false;
      const stickyOffset = 150;
      let nextSectionId = activeSectionId || sections[0]?.id || '';
      sections.forEach((section) => {
        const node = sectionRefs.current[String(section.id)];
        if (!node) return;
        const rect = node.getBoundingClientRect();
        if (rect.top <= stickyOffset) {
          nextSectionId = section.id;
        }
      });
      if (nextSectionId && String(nextSectionId) !== String(activeSectionId)) {
        setActiveSectionId(nextSectionId);
      }
    };
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(resolveActiveSectionFromScroll);
    };
    resolveActiveSectionFromScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSectionId, activeSubcategoryId, searchQuery, sections]);

  useEffect(() => {
    const key = activeSubcategoryId === 'recommended'
      ? 'recommended'
      : activeSectionId === 'menus'
        ? 'menus'
        : `section-${activeSectionId}`;
    const node = tabRefs.current[key];
    if (!node) return;
    window.requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    });
  }, [activeSectionId, activeSubcategoryId]);

  const selectSection = (sectionId) => {
    setActiveSectionId(sectionId);
    setActiveSubcategoryId('all');
    window.requestAnimationFrame(() => {
      sectionRefs.current[String(sectionId)]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const subtotal = cart.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);
  const itemCount = cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const verifiedCustomer = identitySlug === slug && !!identityToken && currentCustomer?.loyaltyEnabled === true;
  const availablePoints = verifiedCustomer ? Math.max(0, Number(currentCustomer.availablePoints ?? currentCustomer.points) || 0) : 0;
  const possibleLoyaltyDiscount = Math.min(Math.floor(availablePoints / 30), Math.floor(subtotal));
  const loyaltyDiscount = useLoyalty ? possibleLoyaltyDiscount : 0;
  const promoDiscount = useMemo(() => {
    if (!appliedPromoCode) return 0;
    const base = Math.max(0, subtotal - loyaltyDiscount);
    if (appliedPromoCode.rewardType === 'discount_percent') {
      return Math.min(base, base * (Number(appliedPromoCode.rewardValue || 0) / 100));
    }
    if (appliedPromoCode.rewardType === 'discount_euro') {
      return Math.min(base, Number(appliedPromoCode.rewardValue || 0));
    }
    const eligibleIds = new Set([
      appliedPromoCode.product?.id,
      ...(appliedPromoCode.eligibleProducts || []).map((product) => product.id),
    ].filter(Boolean).map(String));
    const eligibleLines = cart.filter((item) => item.productId && eligibleIds.has(String(item.productId)));
    if (!eligibleLines.length) return 0;
    return Math.min(base, Math.min(...eligibleLines.map((item) => Number(item.price || 0))));
  }, [appliedPromoCode, cart, loyaltyDiscount, subtotal]);
  const deliveryFee = fulfillmentType === 'delivery' ? Number(deliveryQuote?.fee || 0) : 0;
  const total = Math.max(0, subtotal - loyaltyDiscount - promoDiscount) + deliveryFee;
  const paymentUnavailable = payload?.checkout?.available === false && total > 0;
  const paymentUnavailableMessage = payload?.checkout?.message || 'Le paiement en ligne est momentanément indisponible. Contactez le restaurant.';
  const freeOrder = cart.length > 0 && total === 0;

  const updateDeliveryAddress = (field, value) => {
    deliveryQuoteRequestRef.current += 1;
    setDeliveryQuoteLoading(false);
    setDeliveryAddress((current) => ({ ...current, [field]: value }));
    setDeliveryQuote(null);
    setDeliveryQuoteError('');
  };

  const requestDeliveryQuote = async () => {
    if (!deliveryAddress.addressLine1.trim() || !deliveryAddress.postalCode.trim() || !deliveryAddress.city.trim()) {
      setDeliveryQuoteError('Renseigne l’adresse, le code postal et la ville.');
      return;
    }
    const requestId = ++deliveryQuoteRequestRef.current;
    setDeliveryQuoteLoading(true);
    setDeliveryQuoteError('');
    try {
      const response = await fetch(`${API_URL}/api/click-collect/${encodeURIComponent(slug)}/delivery-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: deliveryAddress }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'Devis de livraison impossible.');
      if (requestId === deliveryQuoteRequestRef.current) setDeliveryQuote(data);
    } catch (err) {
      if (requestId !== deliveryQuoteRequestRef.current) return;
      setDeliveryQuote(null);
      setDeliveryQuoteError(err.message || 'Devis de livraison impossible.');
    } finally {
      if (requestId === deliveryQuoteRequestRef.current) setDeliveryQuoteLoading(false);
    }
  };

  const cartSectionNames = useMemo(() => new Set(cart.map((item) => getCanonicalSectionName(item.sectionName))), [cart]);
  const upsellProducts = products
    .filter((product) => {
      const sectionName = getCanonicalSectionName(product.Section?.name);
      if (cart.some((item) => item.productId === product.id)) return false;
      if (!cartSectionNames.has('Boisson') && sectionName === 'Boisson') return true;
      if (!cartSectionNames.has('Dessert') && sectionName === 'Dessert') return true;
      return sectionName === 'Side';
    })
    .slice(0, 6);

  const buildProductCartItem = (product, supplementIds = []) => {
    const selectedSupplements = (product.Supplements || []).filter((supplement) => supplementIds.includes(supplement.id));
    const supplementPrice = selectedSupplements.reduce((sum, supplement) => sum + Number(supplement.price || 0), 0);
    const key = `product-${product.id}-${selectedSupplements.map((supplement) => supplement.id).sort().join('|') || 'base'}-${stockChoiceSignature(stockChoiceFor(product))}`;
    return {
      key,
      productId: product.id,
      stockChoices: product.stockChoices,
      stockChoice: stockChoiceFor(product),
      type: 'product',
      name: product.name,
      image: product.image,
      sectionName: product.Section?.name || '',
      price: Number(product.price || 0) + supplementPrice,
      quantity: 1,
      supplementIds: selectedSupplements.map((supplement) => supplement.id),
      selectedSupplements,
    };
  };

  const pushCartItem = (nextItem) => {
    setCart((current) => {
      const existing = current.find((entry) => entry.key === nextItem.key);
      if (existing) {
        return current.map((entry) => (entry.key === nextItem.key ? { ...entry, quantity: entry.quantity + 1 } : entry));
      }
      return [...current, nextItem];
    });
  };

  const openProduct = (product) => {
    setDetailProduct(withInitialStockChoice(product));
    setSelectedSupplementIds([]);
  };

  const addProduct = (product) => {
    if ((product.Supplements || []).length || product.stockChoices) {
      openProduct(product);
      return;
    }
    pushCartItem(buildProductCartItem(product));
  };

  const confirmProduct = () => {
    if (!detailProduct || !stockChoiceValid(detailProduct)) return;
    pushCartItem(buildProductCartItem(detailProduct, selectedSupplementIds));
    setDetailProduct(null);
    setSelectedSupplementIds([]);
  };

  const handleMenuSelection = (section, product) => {
    setMenuSelectionError('');
    setMenuSelections((current) => {
      const currentForSection = current[section.id] || [];
      const exists = currentForSection.some((entry) => entry.id === product.id);
      if (exists) {
        return { ...current, [section.id]: currentForSection.filter((entry) => entry.id !== product.id) };
      }
      if (currentForSection.length >= getSectionRequiredQuantity(section)) {
        setMenuSelectionError(`Tu as déjà choisi ${getSectionRequiredQuantity(section)} ${section.name}.`);
        return current;
      }
      return { ...current, [section.id]: [...currentForSection, withInitialStockChoice(product)] };
    });
  };

  const openMenu = (menu) => {
    setMenuSelections({});
    setMenuSelectionError('');
    setSelectedMenu(menu);
  };

  const addMenu = () => {
    if (!selectedMenu) return;
    const missing = visibleMenuSections.find((section) => (menuSelections[section.id] || []).length < section.requiredQuantity);
    if (missing) {
      setMenuSelectionError(`Il manque ${missing.requiredQuantity - (menuSelections[missing.id] || []).length} choix dans ${missing.name}.`);
      return;
    }
    const selections = visibleMenuSections.map((section) => ({
      sectionId: section.id,
      sectionName: section.name,
      products: menuSelections[section.id] || [],
    }));
    if (selections.some(s => s.products.some(p => !stockChoiceValid(p)))) { setMenuSelectionError('Choisissez la recette de chaque produit pour continuer.'); return; }
    const additionalPrice = selections.reduce((sum, selection) => sum + selection.products.reduce(
      (lineSum, product) => lineSum + Number(product.MenuProducts?.additionalPrice || 0),
      0
    ), 0);
    const key = `menu-${selectedMenu.id}-${menuStockSignature(selections)}`;
    pushCartItem({
      key,
      type: 'menu',
      menuId: selectedMenu.id,
      name: selectedMenu.name,
      image: selectedMenu.image,
      sectionName: 'Menu',
      price: Number(selectedMenu.price || 0) + additionalPrice,
      quantity: 1,
      selections,
    });
    setSelectedMenu(null);
    setMenuSelections({});
  };

  const updateQuantity = (key, delta) => {
    setCart((current) => current
      .map((item) => (item.key === key ? { ...item, quantity: item.quantity + delta } : item))
      .filter((item) => item.quantity > 0));
  };

  const loginCustomer = async (register = false) => {
    setAccountError('');
    const requestScope = slug;
    const phone = customerPhone.replace(/\D/g, '');
    if (phone.length < 10) {
      setAccountError('Numéro de téléphone invalide.');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/click-collect/${encodeURIComponent(slug)}/customers/${register ? 'register' : 'login'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, firstName: accountFirstName || customerName || '' }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'Connexion impossible.');
      if (!activeRef.current || scopeRef.current !== requestScope) return;
      const customerReference = { id: data.id };
      setCurrentCustomer(customerReference);
      setCustomerName(accountFirstName || customerName);
      setCustomerPhone(phone);
      try { localStorage.setItem(`${CC_CUSTOMER_SESSION_KEY}_${slug}`, JSON.stringify(customerReference)); } catch {}
      setAccountOpen(false);
    } catch (err) {
      if (!activeRef.current || scopeRef.current !== requestScope) return;
      if (!register && /Aucun compte/i.test(err.message || '')) {
        await loginCustomer(true);
        return;
      }
      setAccountError(err.message || 'Connexion impossible.');
    }
  };

  const logoutCustomer = () => {
    identityRequestRef.current += 1;
    setIdentityToken(''); setIdentitySlug(''); setCurrentCustomer(null); setUseLoyalty(false);
    setCustomerName(''); setCustomerPhone(''); saveCustomerIdentityToken(slug, '');
    try { localStorage.removeItem(`${CC_CUSTOMER_SESSION_KEY}_${slug}`); } catch {}
  };

  const applyPromoCode = async () => {
    setPromoCodeError('');
    const requestScope = slug;
    const code = promoCodeInput.replace(/\D/g, '');
    if (!code) {
      setPromoCodeError('Entre un code promo.');
      return;
    }
    if (!customerPhone) {
      setPromoCodeError('Connecte ton compte avec ton téléphone avant le code promo.');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/click-collect/${encodeURIComponent(slug)}/promo-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, phone: customerPhone }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'Code promo invalide.');
      if (activeRef.current && scopeRef.current === requestScope) setAppliedPromoCode(data);
    } catch (err) {
      if (!activeRef.current || scopeRef.current !== requestScope) return;
      setAppliedPromoCode(null);
      setPromoCodeError(err.message || 'Code promo invalide.');
    }
  };

  const submitOrder = async ({ skipUpsell = false } = {}) => {
    if (!cart.length || submittingRef.current) return;
    if (!pickupReady) { setError('Choisissez un créneau de retrait disponible avant de commander.'); setCartOpen(true); return; }
    if (paymentUnavailable) {
      setCartOpen(true);
      return;
    }
    if (fulfillmentType === 'delivery' && (!deliveryQuote || (deliveryQuote.expiresAt && new Date(deliveryQuote.expiresAt).getTime() <= Date.now()))) {
      setError('Calcule et valide d’abord les frais de livraison.');
      setCartOpen(true);
      return;
    }
    if (!skipUpsell && !upsellSeen && upsellProducts.length) {
      setUpsellOpen(true);
      setUpsellSeen(true);
      return;
    }
    submittingRef.current = true;
    const requestScope = slug;
    setSubmitting(true);
    setError('');
    try {
      const orderRequest = {
          items: cart.map((item) => ({
            type: item.type,
            productId: item.productId,
            menuId: item.menuId,
            quantity: item.quantity,
            supplementIds: item.supplementIds || [],
            stockChoice: item.stockChoice || null,
            selections: (item.selections || []).map((selection) => ({
              sectionId: selection.sectionId,
              sectionName: selection.sectionName,
              productIds: selection.products.map((product) => product.id),
              stockChoices: selection.products.map(stockChoiceFor),
            })),
          })),
          customerId: currentCustomer?.id || null,
          customerName: customerName || currentCustomer?.firstName || '',
          customerPhone,
          pickupTime: 'À confirmer par le restaurant',
          ...(fulfillmentType === 'pickup' && availability?.configured ? { pickupMode, ...(pickupMode === 'scheduled' ? { pickupAt } : {}) } : {}),
          customerComment,
          fulfillmentType,
          deliveryAddress: fulfillmentType === 'delivery' ? deliveryAddress : undefined,
          deliveryFee: fulfillmentType === 'delivery' ? deliveryFee : 0,
          loyaltyPointsUsed: loyaltyDiscount * 30,
          loyaltyDiscountAmount: loyaltyDiscount,
          promoCodeCode: appliedPromoCode?.code || '',
        };
      const fingerprint = JSON.stringify(orderRequest);
      if (orderAttemptRef.current?.fingerprint !== fingerprint) {
        orderAttemptRef.current = {
          fingerprint,
          clientRequestId: window.crypto?.randomUUID?.() || `cc-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        };
      }
      const response = await fetch(`${API_URL}/api/click-collect/${encodeURIComponent(slug)}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(verifiedCustomer ? { Authorization: `Bearer ${identityToken}` } : {}) },
        body: JSON.stringify({ ...orderRequest, clientRequestId: orderAttemptRef.current.clientRequestId }),
      });
      const data = await response.json();
      if (!response.ok) {
        if ((response.status === 401 || response.status === 403) && activeRef.current && scopeRef.current === requestScope) {
          setIdentityToken(''); setCurrentCustomer(null); setUseLoyalty(false); saveCustomerIdentityToken(slug, '');
        }
        if (response.status === 409 && fulfillmentType === 'pickup') { setPickupAt(''); setAvailabilityReload(value => value + 1); }
        throw new Error(stockRecipeError(data, 'Commande impossible.'));
      }
      if (!activeRef.current || scopeRef.current !== requestScope) { submittingRef.current = false; setSubmitting(false); return; }
      if (typeof data.paymentUrl !== 'string' || !data.paymentUrl.startsWith('/api/click-collect/') || data.paymentUrl.includes('..')) throw new Error('Lien de paiement indisponible. Contactez le restaurant avant de recommencer.');
      window.location.href = `${API_URL}${data.paymentUrl}`;
    } catch (err) {
      if (activeRef.current && scopeRef.current === requestScope) setError(err.message || 'Commande impossible.');
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading) {
    return <main className="clickcollect-page"><section className="clickcollect-status-card"><h1>Chargement du menu...</h1></section></main>;
  }

  if (error && !payload) {
    return <main className="clickcollect-page"><section className="clickcollect-status-card"><h1>Menu indisponible</h1><p>{error}</p></section></main>;
  }

  return (
    <main
      className={`clickcollect-page ${cartOpen ? 'cart-is-open' : ''}`}
      style={{
        '--cc-primary': payload?.settings?.uiPrimaryColor || '#08383a',
        '--cc-accent': payload?.settings?.uiAccentColor || '#ccb08c',
        '--cc-bg': payload?.settings?.uiBackgroundColor || '#f7f0e4',
        '--cc-text': payload?.settings?.uiTextColor || '#2b180f',
      }}
    >
      <header className="clickcollect-ubereats-head">
        <button type="button" className="clickcollect-ue-icon clickcollect-ue-search-head" onClick={() => setSearchOpen((current) => !current)} aria-label="Recherche">⌕</button>
        <div className="clickcollect-brand-v3"><picture><source media="(max-width:600px)" srcSet="/static/branding-v5/beclick/compact.svg" /><img src="/static/branding-v4/beclick/horizontal.svg" alt="BECLICK" /></picture><strong>{payload?.account?.name || 'Restaurant'}</strong></div>
        <button type="button" className="clickcollect-ue-icon clickcollect-ue-profile" onClick={() => setAccountOpen(true)} aria-label="Coordonnées client">👤</button>
      </header>
      {!embedded && payload?.chatbot?.enabled === true && payload?.chatbot?.url === `/chat/${encodeURIComponent(slug)}` ? <nav className="clickcollect-assistant-link" aria-label="Aide"><a href={payload.chatbot.url}>Assistant du restaurant</a></nav> : null}
      <section className="clickcollect-schedule" aria-label="Choisir le retrait ou la livraison">
        <strong>Quand souhaitez-vous votre commande ?</strong>
        {payload?.delivery?.available && <label>Mode de remise<select disabled={submitting} value={fulfillmentType} onChange={event => changeFulfillment(event.target.value)}><option value="pickup">Retrait sur place</option><option value="delivery">Livraison</option></select></label>}
        {fulfillmentType === 'pickup' && <>
          {availabilityError ? <p role="alert">{availabilityError}</p> : !availability ? <p role="status">Chargement des horaires…</p> : availability.configured ? <>
            <label>Retrait<select disabled={submitting} value={pickupMode} onChange={event => changePickup(event.target.value)}>{(availability.allowedModes || []).map(mode => <option key={mode} value={mode}>{mode === 'scheduled' ? 'Précommander' : 'Au plus tôt'}</option>)}</select></label>
            {pickupMode === 'scheduled' ? <>
              <label>Date de retrait<select disabled={submitting} value={pickupDate} onChange={event => changePickup('scheduled', '', event.target.value)}><option value="">Choisir une date</option>{(availability.dates || []).map(date => <option key={date} value={date}>{date.split('-').reverse().join('/')}</option>)}</select></label>
              <label>Horaire de retrait<select value={pickupAt} disabled={!pickupDate || submitting} onChange={event => changePickup('scheduled', event.target.value)}><option value="">Choisir un créneau</option>{(availability.slots || []).map(slot => <option key={slot.id} value={slot.pickupAt} disabled={!slot.available}>{new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZoneName: 'shortOffset', timeZone: availability.timezone }).format(new Date(slot.pickupAt))}{slot.available ? '' : ' — complet'}</option>)}</select></label>
              <p>Heure locale du restaurant : {availability.timezone}. Le créneau est réservé temporairement au passage au paiement.</p>
            </> : <p>{availability.immediate?.available ? 'Retrait au plus tôt disponible.' : 'Le retrait au plus tôt est indisponible.'}</p>}
            {availability.reason && <p role="status">{availability.reason}</p>}
            <p>Changer de mode ou d’horaire vide le panier pour vérifier les plats disponibles.</p>
          </> : <p>Le restaurant confirme le délai de préparation après réception.</p>}
          <button type="button" disabled={submitting} onClick={() => setAvailabilityReload(value => value + 1)}>Actualiser les créneaux</button>
        </>}
        {fulfillmentType === 'delivery' && <p>Livraison immédiate. Validez votre adresse et les frais dans le panier.</p>}
      </section>
      <div className={`clickcollect-search-row ${searchOpen ? 'is-open' : ''}`}>
        <button type="button" onClick={() => setSearchOpen((current) => !current)} aria-label="Recherche">⌕</button>
        <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Rechercher un plat" />
      </div>

      <fieldset disabled={!pickupReady || submitting} style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: 'contents' }} aria-label="Catalogue et panier">
      <div className="clickcollect-sticky-menu clickcollect-ue-tabs">
        <button type="button" className="clickcollect-ue-browse" onClick={() => setMobileMenuOpen(true)} aria-label="Parcourir le menu">☰</button>
        <nav className="clickcollect-tabs" aria-label="Catégories">
          <button
            type="button"
            ref={(node) => { tabRefs.current.recommended = node; }}
            className={!searchQuery && activeSectionId !== 'menus' && activeSubcategoryId === 'recommended' ? 'active' : ''}
            onClick={() => { setActiveSubcategoryId('recommended'); setActiveSectionId(sections[0]?.id || activeSectionId); }}
          >
            Notre sélection pour vous
          </button>
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              ref={(node) => { tabRefs.current[`section-${section.id}`] = node; }}
              className={String(activeSectionId) === String(section.id) ? 'active' : ''}
              onClick={() => selectSection(section.id)}
            >
              {section.name}
            </button>
          ))}
          {menus.length ? (
            <button
              type="button"
              ref={(node) => { tabRefs.current.menus = node; }}
              className={activeSectionId === 'menus' ? 'active' : ''}
              onClick={() => setActiveSectionId('menus')}
            >
              Menus
            </button>
          ) : null}
        </nav>
        {activeSectionId !== 'menus' && (
          <nav className="clickcollect-subtabs" aria-label="Sous-catégories">
            <button type="button" className={activeSubcategoryId === 'all' ? 'active' : ''} onClick={() => setActiveSubcategoryId('all')}>Tout</button>
            {sectionSubcategories.map((subcategory) => (
              <button key={subcategory.id} type="button" className={String(activeSubcategoryId) === String(subcategory.id) ? 'active' : ''} onClick={() => setActiveSubcategoryId(subcategory.id)}>
                {subcategory.name}
              </button>
            ))}
          </nav>
        )}
      </div>
      {mobileMenuOpen ? (
        <div className="clickcollect-drawer-layer">
          <button type="button" className="clickcollect-drawer-backdrop" aria-label="Fermer le menu" onClick={() => setMobileMenuOpen(false)} />
          <aside className="clickcollect-drawer" aria-label="Menu mobile">
            <div className="clickcollect-drawer-head">
              <strong>Menu</strong>
              <button type="button" onClick={() => setMobileMenuOpen(false)}>×</button>
            </div>
            <div className="clickcollect-drawer-list">
              {sections.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  className={String(activeSectionId) === String(section.id) ? 'active' : ''}
                  onClick={() => {
                    selectSection(section.id);
                    setMobileMenuOpen(false);
                  }}
                >
                  {section.name}
                </button>
              ))}
              {menus.length ? (
                <button
                  type="button"
                  className={activeSectionId === 'menus' ? 'active' : ''}
                  onClick={() => {
                    setActiveSectionId('menus');
                    setMobileMenuOpen(false);
                  }}
                >
                  Menus
                </button>
              ) : null}
            </div>
            {activeSectionId !== 'menus' && sectionSubcategories.length > 0 ? (
              <>
                <span className="clickcollect-drawer-kicker">Sous-catégories</span>
                <div className="clickcollect-drawer-list is-sub">
                  <button type="button" className={activeSubcategoryId === 'all' ? 'active' : ''} onClick={() => { setActiveSubcategoryId('all'); setMobileMenuOpen(false); }}>Tout</button>
                  {sectionSubcategories.map((subcategory) => (
                    <button
                      key={subcategory.id}
                      type="button"
                      className={String(activeSubcategoryId) === String(subcategory.id) ? 'active' : ''}
                      onClick={() => {
                        setActiveSubcategoryId(subcategory.id);
                        setMobileMenuOpen(false);
                      }}
                    >
                      {subcategory.name}
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </aside>
        </div>
      ) : null}

      <section className="clickcollect-layout">
        <div className="clickcollect-products">
          {activeSectionId === 'menus' ? menus.map((menu) => (
            <article key={`menu-${menu.id}`} className="clickcollect-product is-menu">
              <button type="button" className="clickcollect-product-image" onClick={() => openMenu(menu)}>
                {menu.image ? <img src={resolveMediaUrl(menu.image)} alt="" /> : <span>M</span>}
              </button>
              <div className="clickcollect-product-copy"><span>Menu</span><h2>{menu.name}</h2><p>{formatDescription(menu.description)}</p><strong>{formatCurrency(menu.price)}</strong></div>
              <button type="button" onClick={() => openMenu(menu)}>+</button>
            </article>
          )) : browseGroups.map((group) => (
            <React.Fragment key={group.id}>
              <h2
                className="clickcollect-section-title"
                ref={group.sectionId ? (node) => { sectionRefs.current[String(group.sectionId)] = node; } : undefined}
              >
                {group.title}
              </h2>
              {group.products.map((product) => (
                <article key={`product-${group.id}-${product.id}`} className="clickcollect-product">
                  <button type="button" className="clickcollect-product-image" onClick={() => openProduct(product)}>
                    {product.image ? <img src={resolveMediaUrl(product.image)} alt="" /> : <span>{product.name.slice(0, 1)}</span>}
                  </button>
                  <div className="clickcollect-product-copy"><span>{product.Section?.name || 'Produit'}</span><h2>{product.name}</h2><p>{formatDescription(product.description)}</p><strong>{formatCurrency(product.price)}</strong></div>
                  <button type="button" aria-label={`Ajouter ${product.name}`} onClick={() => addProduct(product)}>+</button>
                </article>
              ))}
            </React.Fragment>
          ))}
        </div>

        <aside className="clickcollect-cart" aria-label="Panier">
          <div className="clickcollect-cart-header"><h2>Ta commande</h2><button type="button" className="clickcollect-cart-close" onClick={() => setCartOpen(false)}>Fermer</button></div>
          <p className="clickcollect-muted">{fulfillmentType === 'delivery' ? 'Livraison assurée par Uber Direct.' : pickupMode === 'scheduled' && pickupAt ? `PRÉCOMMANDE — retrait le ${new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short', timeZone: availability?.timezone || 'Europe/Paris' }).format(new Date(pickupAt))} (${availability?.timezone})` : 'Le restaurant confirme le délai de préparation après réception de la commande.'}</p>
          <div className="clickcollect-cart-list">
            {cart.map((item) => (
              <div key={item.key} className="clickcollect-cart-row">
                <div><strong>{item.name}</strong><small>{formatCurrency(item.price)} x {item.quantity}</small>{stockChoiceSummary(item) && <small>{stockChoiceSummary(item)}</small>}{item.selectedSupplements?.map((supplement) => <small key={supplement.id}>+ {supplement.name}</small>)}{item.selections?.map((selection) => <small key={selection.sectionId}>{selection.sectionName}: {selection.products.map((product) => [product.name, stockChoiceSummary(product)].filter(Boolean).join(" — ")).join(', ')}</small>)}</div>
                <div className="clickcollect-qty"><button type="button" onClick={() => updateQuantity(item.key, -1)}>-</button><span>{item.quantity}</span><button type="button" onClick={() => updateQuantity(item.key, 1)}>+</button></div>
              </div>
            ))}
          </div>
          <div className="clickcollect-form">
            <div className="clickcollect-fulfillment-choice">
              <button type="button" className={fulfillmentType === 'pickup' ? 'active' : ''} onClick={() => { if (fulfillmentType !== 'pickup') changeFulfillment('pickup'); setDeliveryQuoteError(''); }}>Retrait sur place</button>
              {payload?.delivery?.available ? (
                <button type="button" className={fulfillmentType === 'delivery' ? 'active' : ''} onClick={() => changeFulfillment('delivery')}>Livraison Uber Direct</button>
              ) : payload?.delivery?.enabled ? <button type="button" disabled>Livraison bientôt disponible</button> : null}
            </div>
            <label>Nom pour la commande<input value={customerName} onChange={(event) => setCustomerName(event.target.value)} placeholder="Ex: Jordy" /></label>
            <label>Téléphone<input value={customerPhone} onChange={(event) => setCustomerPhone(event.target.value)} placeholder="Obligatoire pour les promos" /></label>
            {fulfillmentType === 'delivery' ? (
              <div className="clickcollect-delivery-fields">
                <label>Adresse<input value={deliveryAddress.addressLine1} onChange={(event) => updateDeliveryAddress('addressLine1', event.target.value)} placeholder="Numéro et rue" /></label>
                <label>Complément<input value={deliveryAddress.addressLine2} onChange={(event) => updateDeliveryAddress('addressLine2', event.target.value)} placeholder="Bâtiment, étage…" /></label>
                <div className="clickcollect-delivery-city"><label>Code postal<input value={deliveryAddress.postalCode} inputMode="numeric" onChange={(event) => updateDeliveryAddress('postalCode', event.target.value)} /></label><label>Ville<input value={deliveryAddress.city} onChange={(event) => updateDeliveryAddress('city', event.target.value)} /></label></div>
                <label>Instructions livreur<input value={deliveryAddress.instructions} onChange={(event) => updateDeliveryAddress('instructions', event.target.value)} placeholder="Digicode, porte…" /></label>
                <button className="clickcollect-secondary" type="button" onClick={requestDeliveryQuote} disabled={deliveryQuoteLoading}>{deliveryQuoteLoading ? 'Calcul en cours…' : 'Calculer les frais de livraison'}</button>
                {deliveryQuoteError ? <p className="clickcollect-error">{deliveryQuoteError}</p> : null}
                {deliveryQuote ? <p className="clickcollect-delivery-quote"><strong>{formatCurrency(deliveryQuote.fee)}</strong> de frais de livraison · arrivée estimée {deliveryQuote.dropoffEta ? new Date(deliveryQuote.dropoffEta).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : `dans ${deliveryQuote.durationMinutes || '?'} min`}</p> : null}
              </div>
            ) : null}
            <label>Note cuisine<input value={customerComment} onChange={(event) => setCustomerComment(event.target.value)} placeholder="Optionnel" /></label>
            {verifiedCustomer ? <>
              <p className="clickcollect-muted">{availablePoints} points disponibles.</p>
              {possibleLoyaltyDiscount > 0 ? <label className="clickcollect-check"><input type="checkbox" checked={useLoyalty} onChange={(event) => setUseLoyalty(event.target.checked)} />Utiliser {possibleLoyaltyDiscount * 30} points (−{formatCurrency(possibleLoyaltyDiscount)})</label> : null}
            </> : payload?.identity?.available ? <button type="button" onClick={() => setAccountOpen(true)}>Vérifier mon téléphone pour utiliser mes points</button> : <p className="clickcollect-muted">Pour utiliser vos avantages fidélité, faites vérifier votre compte par le restaurant.</p>}
            <div className="clickcollect-promo-row"><input value={promoCodeInput} onChange={(event) => { setPromoCodeInput(event.target.value); setPromoCodeError(''); }} placeholder="Code promo" inputMode="numeric" /><button type="button" onClick={applyPromoCode}>OK</button></div>
            {promoCodeError ? <p className="clickcollect-error">{promoCodeError}</p> : null}
            {appliedPromoCode ? <p className="clickcollect-muted">Code {appliedPromoCode.code} appliqué.</p> : null}
          </div>
          {error ? <p className="clickcollect-error">{error}</p> : null}
          <div className="clickcollect-total"><span>Sous-total</span><strong>{formatCurrency(subtotal)}</strong></div>
          {loyaltyDiscount > 0 ? <div className="clickcollect-total"><span>Fidélité</span><strong>-{formatCurrency(loyaltyDiscount)}</strong></div> : null}
          {promoDiscount > 0 ? <div className="clickcollect-total"><span>Code promo</span><strong>-{formatCurrency(promoDiscount)}</strong></div> : null}
          {deliveryFee > 0 ? <div className="clickcollect-total"><span>Livraison Uber Direct</span><strong>{formatCurrency(deliveryFee)}</strong></div> : null}
          <div className="clickcollect-total"><span>{itemCount} article(s)</span><strong>{formatCurrency(total)}</strong></div>
          {paymentUnavailable ? <p className="clickcollect-error" role="status">{paymentUnavailableMessage}</p> : null}
          <button className="clickcollect-primary" type="button" onClick={() => submitOrder()} disabled={!cart.length || submitting || paymentUnavailable || !pickupReady}>{submitting ? (freeOrder ? 'Confirmation en cours...' : 'Redirection paiement...') : (freeOrder ? 'Confirmer sans paiement' : 'Payer maintenant')}</button>
        </aside>
      </section>

      </fieldset>
      {cart.length ? <button type="button" className="clickcollect-floating-cart" onClick={() => setCartOpen(true)}><span>{itemCount}</span><strong>Voir panier</strong><em>{formatCurrency(total)}</em></button> : null}
      {cartOpen ? <button type="button" className="clickcollect-cart-backdrop" aria-label="Fermer panier" onClick={() => setCartOpen(false)} /> : null}

      {detailProduct ? <div className="clickcollect-modal-backdrop" onClick={() => setDetailProduct(null)}><div className="clickcollect-modal clickcollect-product-modal" onClick={(event) => event.stopPropagation()}><button type="button" className="clickcollect-modal-close" onClick={() => setDetailProduct(null)}>×</button><img className="clickcollect-modal-image" src={resolveMediaUrl(detailProduct.image) || 'https://via.placeholder.com/900x600?text=Produit'} alt="" /><h2>{detailProduct.name}</h2><p>{formatDescription(detailProduct.description)}</p><strong>{formatCurrency(detailProduct.price)}</strong>{detailProduct.allergens?.length ? <div className="clickcollect-allergens"><strong>Allergènes</strong><p>{detailProduct.allergens.map((id) => PRODUCT_ALLERGEN_LABELS[id] || id).join(', ')}</p></div> : null}<StockRecipeChoices product={detailProduct} onChange={stockChoice => setDetailProduct(current => ({ ...current, stockChoice }))} />{groupSupplements(detailProduct).map((group) => <section key={group.name} className="clickcollect-supplement-group"><h3>{group.name}</h3>{group.items.map((supplement) => <label key={supplement.id} className="clickcollect-check"><input type="checkbox" checked={selectedSupplementIds.includes(supplement.id)} onChange={() => setSelectedSupplementIds((current) => current.includes(supplement.id) ? current.filter((id) => id !== supplement.id) : [...current, supplement.id])} /> {supplement.name} (+{formatCurrency(supplement.price)})</label>)}</section>)}<button type="button" className="clickcollect-primary" disabled={!stockChoiceValid(detailProduct)} onClick={confirmProduct}>Ajouter au panier</button></div></div> : null}

      {selectedMenu ? <div className="clickcollect-modal-backdrop" onClick={() => setSelectedMenu(null)}><div className="clickcollect-modal" onClick={(event) => event.stopPropagation()}><button type="button" className="clickcollect-modal-close" onClick={() => setSelectedMenu(null)}>×</button><h2>{selectedMenu.name}</h2>{visibleMenuSections.map((section) => <section key={section.id} className="clickcollect-supplement-group"><h3>{section.name} · {getSectionRequiredQuantity(section)} choix</h3><div className="clickcollect-menu-choice-grid">{section.products.map((product) => { const selected = (menuSelections[section.id] || []).some((entry) => entry.id === product.id); return <button key={product.id} type="button" className={selected ? 'active' : ''} onClick={() => handleMenuSelection(section, product)}>{product.image ? <img src={resolveMediaUrl(product.image)} alt="" /> : null}<strong>{product.name}</strong><span>+{formatCurrency(product.MenuProducts?.additionalPrice || 0)}</span></button>; })}</div>{(menuSelections[section.id] || []).map(product => <StockRecipeChoices key={product.id} product={product} onChange={stockChoice => setMenuSelections(current => ({ ...current, [section.id]: current[section.id].map(p => p.id === product.id ? { ...p, stockChoice } : p) }))} />)}</section>)}{menuSelectionError ? <p className="clickcollect-error">{menuSelectionError}</p> : null}<button type="button" className="clickcollect-primary" onClick={addMenu}>Ajouter ce menu</button></div></div> : null}

      {upsellOpen ? <div className="clickcollect-modal-backdrop" onClick={() => setUpsellOpen(false)}><div className="clickcollect-modal" onClick={(event) => event.stopPropagation()}><button type="button" className="clickcollect-modal-close" onClick={() => setUpsellOpen(false)}>×</button><h2>Une dernière envie ?</h2><div className="clickcollect-menu-choice-grid">{upsellProducts.map((product) => <button key={product.id} type="button" onClick={() => addProduct(product)}>{product.image ? <img src={resolveMediaUrl(product.image)} alt="" /> : null}<strong>{product.name}</strong><span>{formatCurrency(product.price)}</span></button>)}</div>{paymentUnavailable ? <p className="clickcollect-error" role="status">{paymentUnavailableMessage}</p> : null}<button type="button" className="clickcollect-primary" disabled={submitting || paymentUnavailable} onClick={() => { setUpsellOpen(false); submitOrder({ skipUpsell: true }); }}>{freeOrder ? 'Confirmer sans paiement' : 'Valider et payer'}</button></div></div> : null}

      {accountOpen ? <div className="clickcollect-modal-backdrop" onClick={() => setAccountOpen(false)}><div className="clickcollect-modal clickcollect-account-modal" onClick={(event) => event.stopPropagation()}><button type="button" className="clickcollect-modal-close" onClick={() => setAccountOpen(false)}>×</button><h2>{payload?.identity?.available ? 'Mon compte fidélité' : 'Mes coordonnées'}</h2>
        {verifiedCustomer ? <><p>Bonjour {currentCustomer.firstName || 'et bienvenue'}, votre téléphone est vérifié.</p><p>{availablePoints} points disponibles.</p><button type="button" className="clickcollect-primary" onClick={logoutCustomer}>Me déconnecter du restaurant</button><p>Cette déconnexion ne ferme pas votre session BEFID sur cet appareil.</p><BefidCustomerIdentityPanel slug={slug} token={identityToken} onReverify={() => { logoutCustomer(); setAccountOpen(true); }} /></>
          : payload?.identity?.available ? <ClickCollectIdentity key={slug} slug={slug} initialPhone={customerPhone} onVerified={(data) => {
            identityRequestRef.current += 1;
            setIdentityToken(data.token); setIdentitySlug(slug); setCurrentCustomer(data.customer);
            setCustomerName(data.customer.firstName || ''); setCustomerPhone(data.customer.phone || '');
            setUseLoyalty(false); setAccountOpen(false);
          }} />
            : currentCustomer ? <><p>Votre référence client est enregistrée sur cet appareil.</p><p>Les avantages fidélité doivent être vérifiés par le restaurant.</p><button type="button" className="clickcollect-primary" onClick={logoutCustomer}>Effacer ma référence</button></>
              : <><label>Téléphone<input value={customerPhone} onChange={(event) => setCustomerPhone(event.target.value)} placeholder="06..." /></label><label>Prénom<input value={accountFirstName} onChange={(event) => setAccountFirstName(event.target.value)} placeholder="Optionnel" /></label>{accountError ? <p className="clickcollect-error">{accountError}</p> : null}<button type="button" className="clickcollect-primary" onClick={() => loginCustomer(false)}>Enregistrer mes coordonnées</button></>}
      </div></div> : null}
    </main>
  );
}

export { ClickCollectStatus };
export default ClickCollectView;
